// Command-mode navigation. The drawer is a flat
// vertical list and navigation is MODELESS: ↑/↓ move the focus ring one control at
// a time, ←/→ dial the focused control IN PLACE (slider step, dropdown cycle,
// checkbox set), ● activates buttons. No select→adjust→blur — the same handlers the
// mouse fires run unchanged. `navStep` stays a pure, column-generic index helper.

import type { NavKey } from "../input/command-layer";

type ArrowKey = "up" | "down" | "left" | "right";

/** Next cursor index for an arrow move over a row-major grid; clamps at edges. */
export function navStep(index: number, total: number, columns: number, key: ArrowKey): number {
  if (total <= 0) return 0;
  const clamp = (i: number): number => Math.max(0, Math.min(total - 1, i));
  switch (key) {
    case "left":
      return clamp(index - 1);
    case "right":
      return clamp(index + 1);
    case "up": {
      const i = index - columns;
      return i >= 0 ? i : index;
    }
    case "down": {
      const i = index + columns;
      return i < total ? i : index;
    }
  }
}

const FOCUS_CLASS = "nav-focus";
const CELL_SELECTOR = "input, select, button";

/** Drives a modeless focus cursor over the drawer's controls from NavKey events. */
export class CommandNav {
  private cells: HTMLElement[] = [];
  /** Focus index — deliberately NOT reset on open/close, so reopening lands where
   *  you left off (ergonomic for iterative tuning from the bench). */
  private index = 0;

  constructor(private readonly drawer: HTMLElement) {}

  open(): void {
    this.drawer.classList.add("open");
    this.refresh();
  }

  close(): void {
    this.clearRing();
    this.drawer.classList.remove("open");
  }

  /** Re-collect cells (the results section and devices can change). */
  private refresh(): void {
    this.cells = [...this.drawer.querySelectorAll<HTMLElement>(CELL_SELECTOR)].filter(
      (el) => !el.classList.contains("menu-btn") && !isHidden(el),
    );
    if (this.index >= this.cells.length) this.index = Math.max(0, this.cells.length - 1);
    this.applyRing();
  }

  handle(key: NavKey): void {
    if (this.cells.length === 0) return;
    if (key === "up" || key === "down") {
      this.index = navStep(this.index, this.cells.length, 1, key); // flat list ⇒ ±1
    } else if (key === "left") {
      this.adjust(-1);
    } else if (key === "right") {
      this.adjust(1);
    } else {
      this.activate();
    }
    this.applyRing();
  }

  private current(): HTMLElement | undefined {
    return this.cells[this.index];
  }

  /** ● activates a button / file input, or toggles a checkbox. Sliders and dropdowns
   *  are not "activated" — they're dialed by ←/→ (modeless, no edit submode). */
  private activate(): void {
    const el = this.current();
    if (!el) return;
    if (el instanceof HTMLButtonElement) el.click();
    else if (el instanceof HTMLInputElement && (el.type === "checkbox" || el.type === "file")) {
      el.click();
    }
  }

  /** ←/→ dials the focused control in place: slider/number step, dropdown cycle,
   *  checkbox set (← off, → on). Fires the real input/change events. */
  private adjust(dir: -1 | 1): void {
    const el = this.current();
    if (el instanceof HTMLInputElement) {
      if (el.type === "checkbox") {
        el.checked = dir > 0;
        el.dispatchEvent(new Event("change", { bubbles: true }));
      } else {
        if (dir > 0) el.stepUp();
        else el.stepDown();
        el.dispatchEvent(new Event("input", { bubbles: true }));
        el.dispatchEvent(new Event("change", { bubbles: true }));
      }
    } else if (el instanceof HTMLSelectElement) {
      const next = Math.max(0, Math.min(el.options.length - 1, el.selectedIndex + dir));
      if (next === el.selectedIndex) return;
      el.selectedIndex = next;
      el.dispatchEvent(new Event("change", { bubbles: true }));
    }
  }

  private applyRing(): void {
    this.clearRing();
    const el = this.current();
    if (!el) return;
    el.classList.add(FOCUS_CLASS);
    el.scrollIntoView({ block: "nearest" });
  }

  private clearRing(): void {
    for (const el of this.cells) el.classList.remove(FOCUS_CLASS);
  }
}

function isHidden(el: HTMLElement): boolean {
  // offsetParent === null already means "not rendered" while the drawer is open — and it
  // covers display:none on the element OR any ancestor (e.g. the device <select> hidden via
  // its parent field). The old `&& el.style.display === "none"` missed the parent-hidden case
  // and left an invisible control in the D-pad cell list.
  return el.offsetParent === null;
}
